import { createSelector } from 'reselect';
import { GEO_LOCATION, OBJECT } from 'utils/fieldTypes';
import { QueryBuilderIndex, ValueType } from './types';
import { isTriggerValueType, getFilteredFields } from './utils';

/**
 * baseSelector
 * Selector for the query builder state.
 * @param { Object } state: The application state.
 * @returns { Object } The query builder state.
 */
export const queryBuilderSelector = (state) => state.queryBuilder;

/**
 * propsSelector
 * Selector for the application props.
 * @param { Object } state: The application state.
 * @param { Object } props: The application props.
 * @returns { Object } The application props.
 */
export const propsSelector = (_, props) => props;

export const getAttr = (attr) => {
  return (state) => {
    const queryBuilderState = queryBuilderSelector(state);
    return queryBuilderState?.[attr];
  };
};

/**
 * ifValueExists
 * Helper turnery function that returns specific values if givem value exists.
 * @param { String } value: The value to check if it exists.
 * @param { String } ifExists: The return value of value exists.
 * @param { String } ifDoesNotExist: The return value if value does not exist.
 * @returns { String } The conditional return value.
 */
export const ifValueExists = (value, ifExists, ifDoesNotExist) =>
  value ? ifExists : ifDoesNotExist;

/*------------------------------------------------------------------------------------------------*/
/* Base Search Query Selectors                                                                    */
/*------------------------------------------------------------------------------------------------*/
export const selectCombinator = getAttr('combinator');
export const selectComparatorDropdownProps = getAttr('comparatorDropdownProps');
export const selectEmptySearchQuery = getAttr('emptySearchQuery');
export const selectEmptyCriteriaProperty = getAttr('emptyCriteriaProperty');
export const selectFields = getAttr('fields');
export const selectFieldOptions = getAttr('fieldOptions');
export const selectHasDynamicFields = getAttr('hasDynamicFields');
export const selectHideProperty = getAttr('hideProperty');
export const selectIsViewMode = getAttr('isViewMode');
export const selectOrderBy = getAttr('orderBy');
export const selectPropertyPlaceholder = getAttr('propertyPlaceholder');
export const selectSearchGroups = getAttr('searchQueries');
export const selectPresentState = getAttr('presentState');
export const selectShowDynamicFieldToggle = getAttr('showDynamicFieldToggle');
export const selectShowValidations = getAttr('showValidations');
export const selectSizeLimit = getAttr('sizeLimit');
export const selectSizeLimitMax = getAttr('sizeLimitMax');
export const selectType = getAttr('type');
export const selectRedoQueue = getAttr('redoQueue');
export const selectUndoStack = getAttr('undoStack');
export const selectValidationMessages = getAttr('validationMessages');
export const selectUsesSmartRecos = getAttr('usesSmartRecos');
export const selectCustomDateTextInput = getAttr('showCustomDateTextInput');
export const selectCloningValidationMessages = getAttr('cloningValidationMessages');
export const selectEsIndex = getAttr('esIndex');
export const selectDisallowHandlebarsInQuery = getAttr('disallowHandlebarsInQuery');
export const selectCatalogName = getAttr('catalogName');

export const selectCanUseValueTypeahead = createSelector(
  [selectEsIndex],
  (esIndex) => esIndex === QueryBuilderIndex.UserAndEvent,
);
export const selectCanRedo = createSelector([selectRedoQueue], (redoQueue) => redoQueue.length > 0);
export const selectCanUndo = createSelector([selectUndoStack], (undoStack) => undoStack.length > 0);

/*------------------------------------------------------------------------------------------------*/
/* Limit And Order By Selectors                                                                   */
/*------------------------------------------------------------------------------------------------*/
export const selectOrderByDynamicLocation = (state) => selectOrderBy(state)?.dynamicLocation;
export const selectOrderByField = (state) => selectOrderBy(state)?.field;
export const selectOrderByIsSortAsc = (state) => selectOrderBy(state)?.asc;
export const selectOrderByLocation = (state) => selectOrderBy(state)?.location;

export const selectHasLocation = createSelector(
  [selectOrderByDynamicLocation, selectOrderByLocation],
  (dynamicLocation, location) => dynamicLocation !== undefined || location?.lat !== undefined,
);

/*------------------------------------------------------------------------------------------------*/
/* Search Group Selectors                                                                         */
/*------------------------------------------------------------------------------------------------*/
export const selectSearchGroup = createSelector(
  [selectSearchGroups, propsSelector],
  (searchGroups, { searchGroupIndex }) => searchGroups[searchGroupIndex],
);

export const selectSearchGroupCombinator = createSelector(
  [selectSearchGroup],
  (searchGroup) => searchGroup?.combinator,
);

/*------------------------------------------------------------------------------------------------*/
/* Search Query Selectors                                                                         */
/*------------------------------------------------------------------------------------------------*/
export const selectSearchQueries = createSelector(
  [selectSearchGroup],
  (searchGroup) => searchGroup?.searchQueries || [],
);

export const selectSearchQuery = createSelector(
  [selectSearchQueries, propsSelector],
  (searchQueries, { searchQueryIndex }) => {
    return searchQueries[searchQueryIndex] || {};
  },
);

export const selectCanDeleteSearchQuery = createSelector(
  [selectSearchQueries],
  (searchQueries) => searchQueries.length > 1,
);

export const selectProperty = createSelector(
  [selectSearchQuery],
  (searchQuery) => searchQuery?.dataType,
);

export const selectPropertyRefinement = createSelector([selectSearchQuery], (searchQuery) => ({
  minMatch: searchQuery?.minMatch,
  maxMatch: searchQuery?.maxMatch,
}));

export const selectAllFields = createSelector([selectFields], (fields) => {
  let allFields = [];
  Object.keys(fields).forEach((field) => {
    allFields = [...allFields, ...fields[field].filter((f) => f.type !== OBJECT)];
  });
  return allFields;
});

export const selectFieldsForProperty = createSelector(
  [selectFields, selectProperty, propsSelector],
  (fields, property, { filteredFieldsByPropertyMap }) =>
    filteredFieldsByPropertyMap?.[property]
      ? getFilteredFields(fields[property], filteredFieldsByPropertyMap[property])
      : fields[property],
);

export const selectFieldOptionsForProperty = createSelector(
  [selectFieldOptions, selectProperty],
  (fieldOptions, property) => fieldOptions[property] || {},
);

export const selectSearchCombo = createSelector(
  [selectSearchQuery],
  (searchQuery) => searchQuery?.searchCombo,
);

export const selectSearchQueryCombinator = createSelector(
  [selectSearchCombo],
  (searchCombo) => searchCombo?.combinator,
);

/*------------------------------------------------------------------------------------------------*/
/* Criteria Property Selectors                                                                    */
/*------------------------------------------------------------------------------------------------*/
export const selectCriteriaProperties = createSelector(
  [selectSearchCombo, propsSelector],
  (searchCombo) => searchCombo?.searchQueries || [],
);

export const selectCriteriaProperty = createSelector(
  [selectCriteriaProperties, propsSelector],
  (criteriaProperties, { criteriaPropertyIndex }) => {
    return criteriaProperties[criteriaPropertyIndex] || {};
  },
);

export const selectCanDeleteCriteriaProperty = createSelector(
  [selectCriteriaProperties],
  (criteriaProperties) => criteriaProperties.length > 1,
);

export const selectComparator = createSelector(
  [selectCriteriaProperty],
  (criteriaProperty) => criteriaProperty?.comparatorType,
);

export const selectDistance = createSelector(
  [selectCriteriaProperty],
  (criteriaProperty) => criteriaProperty?.distance,
);

export const selectDynamicValue = createSelector(
  [selectCriteriaProperty],
  (criteriaProperty) => criteriaProperty?.dynamicValue,
);

/**
 * selectValueType
 * This returns the value type for the specified criteria property. If there is a value type, the value
 * type is returned. If there is no value type but there is a dynamic value, a the `contact` value type
 * is returned. If there's no value type and no dynamic value, the `custom` value type is returned
 */
export const selectValueType = createSelector(
  [selectCriteriaProperty],
  ({ valueType, dynamicValue, value }) => {
    if (valueType) {
      return valueType;
    } else if (dynamicValue) {
      return isTriggerValueType(value) ? ValueType.Trigger : ValueType.Contact;
    }
    return ValueType.Custom;
  },
);

export const selectField = createSelector(
  [selectCriteriaProperty],
  (criteriaProperty) => criteriaProperty?.field,
);

export const selectFieldType = createSelector([selectCriteriaProperty], ({ distance, fieldType }) =>
  ifValueExists(!fieldType && distance, GEO_LOCATION, fieldType),
);

export const selectIsClonedData = createSelector(
  [selectCriteriaProperty],
  (criteriaProperty) => !!criteriaProperty?.isClonedData,
);

export const selectLocation = createSelector(
  [selectCriteriaProperty],
  (criteriaProperty) => criteriaProperty?.location,
);

export const selectOptionsForField = createSelector(
  [selectFieldOptionsForProperty, selectField],
  (options, field) => options[field],
);

export const selectValue = createSelector(
  [selectCriteriaProperty],
  (criteriaProperty) => criteriaProperty?.value,
);

export const selectCastType = createSelector(
  [selectCriteriaProperty],
  (criteriaProperty) => criteriaProperty?.castType,
);
